import {AddressResponseData, CountriesFactory} from "@connox/typescript-library";
import * as React from "react";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {getShopId} from "../../../lib/getShopId";
import window, {IAmazonError} from "../../../lib/WindowExtended";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {isSameAddress} from "../AddressStep/AddressStep";
import {amazonPayAddressReadableContainerId, widgetOptions} from "../../../lib/payments/AmazonPayLibrary";
import ListLoader from "../../../utils/loader/ListLoader";
import styles from "./SummaryAddress.module.scss";
import summayStyles from "./Summary.module.scss";
import {INTERNAL_ERROR_TYPE} from "../../../lib/payments/PaymentController";

interface ISummaryAddressProps {
  goToAddressView: () => void;
  onError: (error: Error, occurence: string) => void;
}

interface IAddressInformation {
  deliveryCountryReadable?: string;
  invoiceCountryReadable?: string;
}

const SummaryAddress: React.FC<ISummaryAddressProps> = (props) => {
  const {checkout} = useCheckoutContext();
  const {t} = useTranslation(["checkout"]);
  const [addressInfo, setAddressInfo] = useState<IAddressInformation>({});
  const {deliveryAddress, invoiceAddress} = checkout;
  const {goToAddressView} = props;
  const areAddressesSame = isSameAddress(checkout.invoiceAddress, checkout.deliveryAddress);

  /* retrieve readable country names from codes */
  useEffect(() => {
    if (!invoiceAddress || !deliveryAddress) {
      return;
    }
    void CountriesFactory.getAllCountries(getShopId()).then(() => {
      const invoiceCountryLookup = CountriesFactory.getCountryByCode(invoiceAddress.countryCode);
      const deliveryCountryLookup = CountriesFactory.getCountryByCode(deliveryAddress.countryCode);

      setAddressInfo({
        deliveryCountryReadable: deliveryCountryLookup?.name,
        invoiceCountryReadable: invoiceCountryLookup?.name,
      });
    });
  }, []);

  /*
   * If into Amazon Pay wait for the response of the preAuthorize
   * response in the Summary.tsx useEffect, by just showing the list loader
   */
  if (window.isBillingAgreementAddressSet && !window.isPreauthorizingDone) {
    return (
      <AddressWrapper title={`${t("Rechnungsadresse")}/${t("Lieferadresse")}`}>
        <p className={styles.address} style={{textAlign: "center"}}>
          <ListLoader />
        </p>
      </AddressWrapper>
    );
  }

  /*
   * delivery address paragraph preparation
   */
  const DeliveryParagraph = () => {
    const DeliveryContent: React.FC = () => {
      /* starting off Amazon Pay widget init when preauth is done */
      useEffect(() => {
        if (!window.isPreauthorizingDone) {
          return;
        }

        const errorHandling = (err: IAmazonError) => {
          const tempError = new Error(`[AmazonPayReadableAddressWidget] ${err.getErrorCode()}: ${err.getErrorMessage()}`);
          tempError.name = INTERNAL_ERROR_TYPE.AMAZON.readableAddressWidget + "Error";
          props.onError(tempError, "Amazon Readable Address Widget Delivery Address");
        };

        /* Render Amazon Pay Widget into dedicated div */
        new window.OffAmazonPayments.Widgets.AddressBook({
          amazonOrderReferenceId: window.orderReferenceId,
          onError: errorHandling,
          ...widgetOptions,
          displayMode: "Read",
        }).bind(amazonPayAddressReadableContainerId);
      }, [window.isPreauthorizingDone]);

      /* Memoized Amazon Pay address container div */
      const AmazonPayAddressContainer = () => (
        <div id={amazonPayAddressReadableContainerId} style={{height: "150px", marginBottom: "1rem"}}>
          {/* filled by amazon pay */}
        </div>
      );

      if (window.isBillingAgreementAddressSet) {
        // Return Amazon Pay address widget
        const phone = deliveryAddress?.phone;
        const email = deliveryAddress?.email;

        return (
          <React.Fragment>
            <AmazonPayAddressContainer />
            <div className={styles.address}>
              {phone && <div>{t("Tel.: {{phone}}", {phone})}</div>}
              {email && areAddressesSame && <div>{t("E-Mail: {{email}}", {email})}</div>}
            </div>
          </React.Fragment>
        );
      } else if (deliveryAddress) {
        // return internal address
        return (
          <Paragraph
            address={deliveryAddress}
            areSameAddresses={areAddressesSame}
            humanReadableCountry={addressInfo.deliveryCountryReadable}
            isInvoice={false}
          />
        );
      } else {
        // if no address given, show request (should never be the case)
        return <p className={styles.address}>{t("Sie müssen eine Lieferadresse angeben")}</p>;
      }
    };

    const title = areAddressesSame ? `${t("Rechnungsadresse")}/${t("Lieferadresse")}` : t("Lieferadresse");

    return (
      <AddressWrapper
        showArrowBorder={!window.isBillingAgreementAddressSet}
        goToAddressView={window.isBillingAgreementAddressSet ? undefined : goToAddressView}
        title={title}
      >
        <DeliveryContent />
      </AddressWrapper>
    );
  };

  /*
   * if addresses are the same, just show one
   * no need to prepare invoice address
   */
  if (areAddressesSame) {
    return <DeliveryParagraph />;
  }

  // invoice address paragraph preparation
  const InvoiceParagraph: React.FC = () => {
    if (window.isBillingAgreementAddressSet && areAddressesSame) {
      return null;
    }
    const Content: React.FC = () => {
      if (invoiceAddress) {
        // internal address if given
        return (
          <Paragraph
            address={invoiceAddress}
            humanReadableCountry={addressInfo.invoiceCountryReadable}
            isInvoice={true}
          />
        );
      } else {
        // show request to fill in invoice address if not given (should never be the case)
        return <p className={styles.address}>{t("Sie müssen eine Rechnungsadresse angeben")}</p>;
      }
    };

    return (
      <AddressWrapper
        goToAddressView={window.isBillingAgreementAddressSet ? undefined : goToAddressView}
        title={t("Rechnungsadresse")}
      >
        <Content />
      </AddressWrapper>
    );
  };

  return (
    <React.Fragment>
      <InvoiceParagraph />
      <DeliveryParagraph />
    </React.Fragment>
  );
};

export default SummaryAddress;

/**
 * React address container
 * @param props
 * @constructor
 */
const AddressWrapper: React.FC<{
  goToAddressView?: () => void;
  showArrowBorder?: boolean;
  title: string;
}> = ({children, goToAddressView, showArrowBorder = true, title}) => {
  return (
    <React.Fragment>
      <h2>{title}</h2>
      {goToAddressView && (
        <span className={summayStyles.pen} onClick={goToAddressView}>
          <span className="Svg Svg-Icon-Pen">&#160;</span>
        </span>
      )}
      <div className={showArrowBorder ? "ArrowBorder" : undefined}>{children}</div>
    </React.Fragment>
  );
};

/**
 * React address paragraph
 * @param props
 * @constructor
 */
const Paragraph: React.FC<{
  address: AddressResponseData;
  areSameAddresses?: boolean;
  humanReadableCountry?: string;
  isInvoice: boolean;
}> = (props) => {
  const {address, areSameAddresses, humanReadableCountry, isInvoice} = props;
  const {t} = useTranslation("checkout");
  const {city, company, department, email, firstName, lastName, phone, state, street, zip} = address;
  const streetNo = props.address.number;

  return (
    <div className={styles.address}>
      {isInvoice && window.isBillingAgreementAddressSet && (
        <div className={styles.amazonPayLine}>{t("Via Amazon Pay:")}</div>
      )}
      {company && <div>{company}</div>}
      {department && <div>{department}</div>}
      <div>{`${firstName} ${lastName}`}</div>
      {["GB", "FR", "US"].includes(address.countryCode) ? (
        <div>{`${streetNo} ${street}`}</div>
      ) : (
        <div>{`${street} ${streetNo}`}</div>
      )}
      <div>{`${zip} ${city}`}</div>
      <div>{!!state ? `${state}, ${humanReadableCountry || ""}` : humanReadableCountry}</div>
      {(isInvoice || areSameAddresses) && <div>{t("E-Mail: {{email}}", {email})}</div>}
      {phone && <div>{t("Tel.: {{phone}}", {phone})}</div>}
    </div>
  );
};
